/**
 * Accès aux données de la carte 3D d'Etheria.
 *
 * Toutes les requêtes passent par le proxy BFF (origine unique HTTPS) :
 *   - méta   : `/api/map/meta`               (hors carte-live, servi par le BFF public)
 *   - carte  : `/carte-live/api/*`            (proxy vers Etheria_Map, upstream MC)
 *
 * Le renderer (World3D) ne parle qu'à ces chemins relatifs ; aucune origine tierce
 * n'est contactée directement (sauf mc-heads.net pour les têtes, en <img> croisé).
 */

/** Préfixe des routes carte-live proxifiées. */
const LIVE = "/carte-live/api";
/** Route méta (servie hors carte-live par le BFF public). */
const META = "/api/map/meta";

/** Description d'une dimension jouable exposée par la carte. */
export interface MapDimension {
  id: string;
  /** Nom du monde MC (clé `world` des requêtes tuiles). */
  world: string;
  /** Libellé affiché (FR). */
  label?: string;
}

/** Métadonnées globales de la carte (grille, bordure, niveaux de zoom). */
export interface MapMeta {
  dimensions: MapDimension[];
  world: string;
  cellSize: number;
  borderSize: number;
  borderCenterX: number;
  borderCenterZ: number;
  zoomLevels: number[];
  tilePx: number;
}

/** Un joueur en ligne tel que renvoyé par `/api/live`. */
export interface LivePlayer {
  name: string;
  uuid: string;
  world: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  hp: number;
  maxHp: number;
  class?: string;
  rank?: string;
  combat?: boolean;
  afk?: boolean;
}

/** Réponse de `/api/live`. */
export interface LiveData {
  players: LivePlayer[];
  trails?: Record<string, Array<{ x: number; z: number }>>;
  mcTime?: number;
  [k: string]: unknown;
}

/** Empreinte de claim (bloc de chunks revendiqués). */
export interface ClaimMarker {
  cx: number;
  cz: number;
  owner: string;
  color?: string;
}
/** Point de téléportation nommé. */
export interface WarpMarker {
  id: string;
  name: string;
  x: number;
  z: number;
  owner?: string;
}
/** Point d'intérêt générique. */
export interface PoiMarker {
  id: string;
  x: number;
  y?: number;
  z: number;
  type: string;
  label?: string;
  icon?: string;
}
/** Mort notable récente. */
export interface DeathMarker {
  x: number;
  z: number;
  victim: string;
  killer?: string;
  t: number;
}

/** Réponse de `/api/markers`. */
export interface MarkersData {
  claims: ClaimMarker[];
  warps: WarpMarker[];
  spawn: Array<{ x: number; z: number }>;
  pois: PoiMarker[];
  deaths: DeathMarker[];
}

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(url, { credentials: "include", signal });
  if (!res.ok) throw new Error(`HTTP ${res.status} sur ${url}`);
  return (await res.json()) as T;
}

/**
 * Normalise une méta BRUTE (plugin `/carte-live/api/meta` : `dimensions` = string[],
 * ou BFF `/api/map/meta` : forme réduite {worlds, available}) vers {@link MapMeta}.
 * DÉFENSIF : tout champ manquant/mal typé retombe sur {@link DEFAULT_META} — la carte
 * ne doit JAMAIS faire crasher une page (Accueil/Carte) pour une méta incomplète.
 */
function normalizeMeta(raw: unknown): MapMeta {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const dims: MapDimension[] = Array.isArray(r.dimensions)
    ? (r.dimensions as unknown[])
        .map((d): MapDimension | null => {
          if (typeof d === "string") return { id: d, world: d, label: d };
          if (d && typeof d === "object" && typeof (d as MapDimension).world === "string") {
            return d as MapDimension;
          }
          return null;
        })
        .filter((d): d is MapDimension => d !== null)
    : DEFAULT_META.dimensions;
  const zoom = Array.isArray(r.zoomLevels)
    ? (r.zoomLevels as unknown[]).filter((z): z is number => typeof z === "number" && z > 0)
    : [];
  const num = (v: unknown, def: number): number =>
    typeof v === "number" && Number.isFinite(v) ? v : def;
  const world =
    typeof r.world === "string" && r.world.length > 0
      ? r.world
      : (dims[0]?.world ?? DEFAULT_META.world);
  return {
    dimensions: dims.length > 0 ? dims : DEFAULT_META.dimensions,
    world,
    cellSize: num(r.cellSize, DEFAULT_META.cellSize),
    borderSize: num(r.borderSize, DEFAULT_META.borderSize),
    borderCenterX: num(r.borderCenterX, DEFAULT_META.borderCenterX),
    borderCenterZ: num(r.borderCenterZ, DEFAULT_META.borderCenterZ),
    zoomLevels: zoom.length > 0 ? zoom : DEFAULT_META.zoomLevels,
    tilePx: num(r.tilePx, DEFAULT_META.tilePx),
  };
}

/**
 * Charge les métadonnées de la carte (grille, zoom, bordure).
 * Source primaire = la méta BRUTE du plugin via le proxy (`/carte-live/api/meta`,
 * seule à porter zoomLevels/bordure/dimensions) ; repli sur la méta réduite du BFF
 * (`/api/map/meta`) puis sur {@link DEFAULT_META}. Toujours normalisée, ne throw jamais.
 */
export async function fetchMeta(signal?: AbortSignal): Promise<MapMeta> {
  try {
    return normalizeMeta(await getJson<unknown>(`${LIVE}/meta`, signal));
  } catch {
    try {
      return normalizeMeta(await getJson<unknown>(META, signal));
    } catch {
      return { ...DEFAULT_META };
    }
  }
}

/** Charge l'état live (joueurs en ligne). */
export async function fetchLive(signal?: AbortSignal): Promise<LiveData> {
  return getJson<LiveData>(`${LIVE}/live`, signal);
}

/** Charge les markers dans une fenêtre monde donnée. */
export async function fetchMarkers(
  world: string,
  minX: number,
  minZ: number,
  maxX: number,
  maxZ: number,
  signal?: AbortSignal,
): Promise<MarkersData> {
  const qs = new URLSearchParams({
    world,
    minX: String(Math.floor(minX)),
    minZ: String(Math.floor(minZ)),
    maxX: String(Math.ceil(maxX)),
    maxZ: String(Math.ceil(maxZ)),
  });
  const data = await getJson<Partial<MarkersData>>(`${LIVE}/markers?${qs}`, signal);
  return {
    claims: data.claims ?? [],
    warps: data.warps ?? [],
    spawn: data.spawn ?? [],
    pois: data.pois ?? [],
    deaths: data.deaths ?? [],
  };
}

/**
 * URL de la tuile couleur (PNG 256×256) pour un monde/zoom/coordonnées de tuile.
 * `bust` (optionnel) ajoute un paramètre anti-cache pour re-télécharger une tuile
 * fraîche lors des rafraîchissements périodiques (temps réel).
 */
export function tileUrl(world: string, z: number, tx: number, ty: number, bust?: string): string {
  const v = bust ? `&v=${encodeURIComponent(bust)}` : "";
  return `${LIVE}/tile?world=${encodeURIComponent(world)}&z=${z}&tx=${tx}&ty=${ty}${v}`;
}

/** URL du heightmap (octet-stream 256×256 Uint8) pour une tuile. */
export function heightUrl(world: string, z: number, tx: number, ty: number, bust?: string): string {
  const v = bust ? `&v=${encodeURIComponent(bust)}` : "";
  return `${LIVE}/height?world=${encodeURIComponent(world)}&z=${z}&tx=${tx}&ty=${ty}${v}`;
}

/** Décode un octet de hauteur en Y-monde. byte 0 = manquant (renvoie null). */
export function decodeHeight(byte: number): number | null {
  if (byte === 0) return null;
  return (byte / 255) * 384 - 64;
}

/** Charge le heightmap brut d'une tuile (Uint8Array de 256×256), ou null si absent. */
export async function fetchHeight(
  world: string,
  z: number,
  tx: number,
  ty: number,
  signal?: AbortSignal,
  bust?: string,
): Promise<Uint8Array | null> {
  const res = await fetch(heightUrl(world, z, tx, ty, bust), { credentials: "include", signal });
  if (!res.ok) return null;
  const buf = await res.arrayBuffer();
  const arr = new Uint8Array(buf);
  return arr.length >= 256 * 256 ? arr : null;
}

/** Fallback méta si le BFF ne renvoie pas de méta (valeurs du contrat carte). */
export const DEFAULT_META: MapMeta = {
  dimensions: [{ id: "overworld", world: "world", label: "Overworld" }],
  world: "world",
  cellSize: 64,
  borderSize: 15000,
  borderCenterX: 0,
  borderCenterZ: 0,
  zoomLevels: [1, 2, 4, 8],
  tilePx: 256,
};
